import { useState, useEffect, useRef } from "react"
import { Button } from "react-aria-components"
import { httpGet, httpPost } from "../../../modules/Backend"
import { HttpStatusCode } from "axios"
import { EntityNames } from "../../../enums/EntityNames"
import LoadingButton from "../LoadingButton"
import IconBinRed from "../../../assets/icons/icon-bin-red.svg"
import IconPlusBlue from "../../../assets/icons/icon-plus-blue.svg"
import AdobeTextField from "../../AdobeAriaLib/AdobeTextField"
import AdobeNumberField from "../../AdobeAriaLib/AdobeNumberField"
import { modalSignal } from "./ModalContainer"
import { globalCanSubmitSignal, globalSubmittingSignal, globalUpdateResultsSignal } from "../layout/Layout"
import infoIcon from "../../../assets/icons/icon-information.svg"
import { useSignals } from "@preact/signals-react/runtime"

interface ICreateCaseWithPartCasesModal {
    packingBoxId: string
    caseFormData: any // Contains code, year, prefix, notes, quantity, etc. from the form
    onSuccess: (caseData: any) => void
    onCancel: () => void
    clearValues?: Function
}

interface IPartCaseData {
    code: string
    quantity: number | null
}

export default function CreateCaseWithPartCasesModal({
    packingBoxId,
    caseFormData,
    onSuccess,
    onCancel,
    clearValues
}: ICreateCaseWithPartCasesModal) {
    useSignals()

    const [partCases, setPartCases] = useState<IPartCaseData[]>([])
    const [loading, setLoading] = useState<boolean>(false)
    const [creating, setCreating] = useState<boolean>(false)
    const [packingBoxBreadcrumb, setPackingBoxBreadcrumb] = useState<string>('')
    const [newPartCaseIndex, setNewPartCaseIndex] = useState<number | null>(null)
    const inputRefs = useRef<Map<number, HTMLInputElement>>(new Map())

    useEffect(() => {
        loadPackingBoxLocation()
    }, [packingBoxId])

    useEffect(() => {
        if (newPartCaseIndex !== null) {
            requestAnimationFrame(() => {
                const input = inputRefs.current.get(newPartCaseIndex)
                if (input) {
                    input.focus()
                }
                setNewPartCaseIndex(null)
            })
        }
    }, [newPartCaseIndex, partCases])

    async function loadPackingBoxLocation() {
        try {
            const response = await httpGet(`api/${EntityNames.packingBox}/location?id=${packingBoxId}`)
            if (response.status === HttpStatusCode.Ok && response.data) {
                setPackingBoxBreadcrumb(response.data.breadcrumb || '')
            }
        } catch (error) {
            console.error('Failed to fetch packing box location:', error)
        }
    }

    function handlePartCaseChange(index: number, field: 'code' | 'quantity', value: string | number | null) {
        const updated = [...partCases]
        if (field === 'quantity') {
            updated[index] = {
                ...updated[index],
                quantity: value === '' || value === null ? null : (typeof value === 'string' ? parseInt(value) || null : value)
            }
        } else {
            updated[index] = {
                ...updated[index],
                code: value === null ? '' : String(value)
            }
        }
        setPartCases(updated)
    }

    function handleAddNewPartCase() {
        const newIndex = partCases.length
        setPartCases([...partCases, {
            code: '',
            quantity: null
        }])
        setNewPartCaseIndex(newIndex)
    }

    function handleRemovePartCase(index: number) {
        const updated = partCases.filter((_, i) => i !== index)
        setPartCases(updated)
    }

    function handleQuantityKeyDown(index: number, e: React.KeyboardEvent<HTMLInputElement>) {
        const partCase = partCases[index]
        const isLastPartCase = index === partCases.length - 1
        if (isLastPartCase && e.key === 'Tab' && !e.shiftKey) {
            e.preventDefault()
            handleAddNewPartCase()
        }
    }

    function isValid(): boolean {
        // Filter out empty part cases (those with no code)
        const newPartCases = partCases.filter(pc => pc.code && pc.code.trim() !== '')

        // If there are no part cases, that's valid (part cases are optional)
        if (newPartCases.length === 0) {
            return true
        }

        // Check for duplicate part case codes (case-insensitive)
        const allCodes = newPartCases.map(pc => pc.code?.trim().toLowerCase()).filter(code => code !== '')
        const uniqueCodes = new Set(allCodes)
        if (uniqueCodes.size !== allCodes.length) {
            return false
        }

        // Check all quantities are greater than 0
        if (newPartCases.some(pc => pc.quantity === null || pc.quantity === undefined || pc.quantity <= 0)) {
            return false
        }

        return true
    }

    async function handleCreate() {
        setCreating(true)
        try {
            // Prepare part cases data
            const newPartCases = partCases
                .filter(pc => pc.code && pc.code.trim() !== '')
                .map(pc => ({
                    code: pc.code,
                    quantity: pc.quantity!
                }))

            // Build the payload with case data + specimens
            const payload = {
                ...caseFormData,
                parentId: packingBoxId,
                specimens: newPartCases
            }

            // Create the case directly
            const result = await httpPost(`api/${EntityNames.cases}/create`, payload)

            if (result?.status === HttpStatusCode.Created || result?.status === HttpStatusCode.Ok) {
                // Update packing box type in the list if provided
                if (result.data?.packingBoxId && result.data?.packingBoxTypeName) {
                    globalUpdateResultsSignal.value = {
                        id: result.data.packingBoxId,
                        updateChanges: {
                            typeName: result.data.packingBoxTypeName
                        }
                    };
                }

                if (onSuccess) {
                    // Pass case data with specimens (which includes IDs from the API response)
                    onSuccess(result.data)
                }
                if (clearValues) {
                    clearValues()
                }
                globalCanSubmitSignal.value = { allowed: true };
                globalSubmittingSignal.value = { isBusy: false };
                modalSignal.value = undefined
            }
        } catch (error) {
            console.error('Failed to create case:', error)
        } finally {
            setCreating(false)
        }
    }

    return (
        <div className="display--flex flex__direction--column alignItems--center" style={{ gap: '0.5rem' }}>
            <img src={infoIcon} />
            <h1>No case found matching your search criteria</h1>
            <p className="textAlign--center whitespace--pre-line textWrap">
                Please confirm if you want to create it
            </p>

            <p className="textAlign--center" style={{ marginTop: '0.25rem' }}>
                Click the + icon to add any individual part cases if present.
            </p>

            <div className="return-part-cases__list-wrapper" style={{ maxHeight: '120px', overflowY: 'auto', marginTop: '0.25rem' }}>
                {loading && (
                    <div className="return-part-cases__loading-overlay">
                        <div className="loaders loaders__full"></div>
                    </div>
                )}
                <div className="return-part-cases__list">
                    {partCases.map((partCase, index) => (
                        <div
                            key={`part-case-${index}`}
                            className="return-part-cases__row"
                        >
                            <div className="return-part-cases__spacer"></div>

                            <div className="return-part-cases__specimen-code">
                                <AdobeTextField
                                    value={partCase.code || ''}
                                    label="Part case number"
                                    visibleLabel={true}
                                    icon={undefined}
                                    clickableIcon={false}
                                    onChangeText={(value: string) => handlePartCaseChange(index, 'code', value)}
                                    onBlur={undefined}
                                    required={false}
                                    className=""
                                    disabled={creating}
                                    EnterKeyPress={() => { }}
                                    inputRef={(el: HTMLInputElement | null) => {
                                        if (el) {
                                            inputRefs.current.set(index, el)
                                            if (newPartCaseIndex === index) {
                                                requestAnimationFrame(() => {
                                                    el.focus()
                                                })
                                            }
                                        } else {
                                            inputRefs.current.delete(index)
                                        }
                                    }}
                                />
                            </div>

                            <div className="return-part-cases__quantity">
                                <AdobeNumberField
                                    value={partCase.quantity?.toString() || ''}
                                    label="Quantity"
                                    visibleLabel={true}
                                    icon={undefined}
                                    onChangeText={(value: string) => handlePartCaseChange(index, 'quantity', value)}
                                    required={false}
                                    className=""
                                    disabled={creating}
                                    onKeyDown={(e) => handleQuantityKeyDown(index, e)}
                                />
                            </div>

                            <div className="return-part-cases__breadcrumb">
                                <label className="small margin__bottom--xxxs">Current Location</label>
                                <p className="breadcrumb small return-part-cases__breadcrumb-text">
                                    {packingBoxBreadcrumb || ''}
                                </p>
                            </div>

                            <div className="return-part-cases__action">
                                <div className="display--flex flex__direction--column">
                                    <label className="small margin__bottom--xxxs" style={{ visibility: 'hidden' }}>Action</label>
                                    <div className="display--flex alignItems--center">
                                        <Button
                                            className="button button--text-only"
                                            onPress={() => handleRemovePartCase(index)}
                                            isDisabled={creating}>
                                            <img src={IconBinRed} alt="Remove" className="return-part-cases__remove-button-icon" />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}

                    <hr className="return-part-cases__add-divider" />
                    <div className="return-part-cases__add-button-wrapper">
                        <Button
                            className="button button--text-only"
                            onPress={handleAddNewPartCase}
                            isDisabled={creating}>
                            <img src={IconPlusBlue} alt="Add New Part Case" className="return-part-cases__add-button-icon" />
                        </Button>
                    </div>
                </div>
            </div>

            {!isValid() && (
                <p className="error" style={{ fontSize: '0.875rem', lineHeight: '1.2', marginTop: '0.25rem', marginBottom: '0.25rem' }}>All numbers must be unique and all quantities must be provided.</p>
            )}

            <div className="display--flex gap--xs" style={{ marginTop: '0.25rem', marginBottom: '0' }}>
                <Button
                    className="button button--large button--secondary button--with-icon"
                    onPress={() => {
                        globalCanSubmitSignal.value = { allowed: true };
                        globalSubmittingSignal.value = { isBusy: false };
                        modalSignal.value = undefined;
                        if (onCancel) {
                            onCancel();
                        }
                    }}
                    isDisabled={creating || loading}>
                    <p className="button-text">Cancel</p>
                </Button>
                <LoadingButton
                    className="button button--large button--primary button--with-icon"
                    onPress={handleCreate}
                    buttonText={(() => {
                        const partCasesCount = partCases.filter(pc => pc.code && pc.code.trim() !== '').length
                        if (partCasesCount === 0) {
                            return "Create Case"
                        }
                        return `Create case + ${partCasesCount} part case${partCasesCount === 1 ? '' : 's'}`
                    })()}
                    loadingText="Creating"
                    isDisabled={creating || loading || !isValid()}
                    isLoading={creating}>
                    <></>
                </LoadingButton>
            </div>
        </div>
    )
}
